import { useMemo } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { subDays, format } from "date-fns";
import { useContentStore, getItemMeta, EMPTY_ITEMS, EMPTY_KEYWORDS, type ContentItem } from "@/stores/contentStore";
import { FileText, TrendingUp, Bot, Hash } from "lucide-react";

const CHART_TOOLTIP_STYLE = {
  backgroundColor: "hsl(var(--popover))",
  border: "1px solid hsl(var(--border))",
  borderRadius: 6,
  fontSize: 12,
} as const;

interface ContentStatsProps {
  projectId: number;
}

function MetricCard({
  icon: Icon,
  label,
  value,
  sub,
}: {
  icon: React.ElementType;
  label: string;
  value: string | number;
  sub?: string;
}) {
  return (
    <div className="rounded-lg border border-border/40 bg-[rgba(17,17,19,0.6)] p-4 space-y-1">
      <div className="flex items-center gap-2 text-muted-foreground">
        <Icon className="w-4 h-4" />
        <span className="text-xs uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-2xl font-bold">{value}</p>
      {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}

export function ContentStats({ projectId }: ContentStatsProps) {
  const items = useContentStore((s) => s.items[projectId] ?? EMPTY_ITEMS);
  const keywords = useContentStore((s) => s.keywords[projectId] ?? EMPTY_KEYWORDS);

  const metrics = useMemo(() => {
    const published = items.filter((i) => i.stage === "published").length;
    const pipeline = items.filter((i) =>
      ["researching", "outlining", "writing", "reviewing"].includes(i.stage)
    ).length;

    const aiScores = items
      .map((i) => i.ai_detection_score)
      .filter((s): s is number => s !== null && s !== undefined);
    const avgAi = aiScores.length > 0
      ? Math.round(aiScores.reduce((a, b) => a + b, 0) / aiScores.length)
      : null;

    return { published, pipeline, avgAi, keywordsCount: keywords.length };
  }, [items, keywords]);

  const chartData = useMemo(() => {
    const now = new Date();
    // Single-pass: group published items by date key
    const counts: Record<string, number> = {};
    for (const item of items) {
      if (item.published_at) {
        const key = format(new Date(item.published_at), "yyyy-MM-dd");
        counts[key] = (counts[key] || 0) + 1;
      }
    }
    return Array.from({ length: 30 }, (_, i) => {
      const day = subDays(now, 29 - i);
      const key = format(day, "yyyy-MM-dd");
      return { date: format(day, "MMM dd"), published: counts[key] || 0 };
    });
  }, [items]);

  const recentPublished = useMemo(() => {
    return items
      .filter((i): i is ContentItem & { published_at: string } =>
        i.stage === "published" && !!i.published_at
      )
      .sort((a, b) => new Date(b.published_at).getTime() - new Date(a.published_at).getTime())
      .slice(0, 10);
  }, [items]);

  return (
    <div className="space-y-6">
      {/* Metric cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard icon={FileText} label="Published" value={metrics.published} />
        <MetricCard icon={TrendingUp} label="In Pipeline" value={metrics.pipeline} />
        <MetricCard
          icon={Bot}
          label="Avg AI Score"
          value={metrics.avgAi !== null ? `${metrics.avgAi}%` : "—"}
          sub={metrics.avgAi !== null ? (metrics.avgAi <= 20 ? "Low detection" : "Consider humanizing") : undefined}
        />
        <MetricCard icon={Hash} label="Keywords Tracked" value={metrics.keywordsCount} />
      </div>

      {/* Activity chart */}
      <div className="rounded-lg border border-border/40 bg-[rgba(17,17,19,0.6)] p-4" role="img" aria-label="Publishing activity bar chart for the last 30 days">
        <p className="text-xs text-muted-foreground uppercase tracking-wider mb-4">
          Publishing Activity (30 days)
        </p>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={chartData}>
            <XAxis
              dataKey="date"
              tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
              interval={6}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              allowDecimals={false}
              tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
              axisLine={false}
              tickLine={false}
              width={24}
            />
            <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
            <Bar dataKey="published" fill="#10B981" radius={[2, 2, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Recent publications */}
      {recentPublished.length > 0 && (
        <div className="rounded-lg border border-border/40 bg-[rgba(17,17,19,0.6)] p-4">
          <p className="text-xs text-muted-foreground uppercase tracking-wider mb-3">
            Recent Publications
          </p>
          <div className="space-y-2">
            {recentPublished.map((item) => (
              <div key={item.id} className="flex items-center justify-between text-sm">
                <span className="truncate mr-4">{item.title}</span>
                <div className="flex items-center gap-3 shrink-0 text-xs text-muted-foreground">
                  <span>{item.word_count.toLocaleString()} words</span>
                  <span>{format(new Date(item.published_at), "MMM dd")}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
